import { useEffect, useState } from "react";
import axios from "axios";
import { X, History as HistoryIcon } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime } from "@/components/admin/ui";

const EVENT_META = {
  service_area_created: { label: "Service Area Created", category: "status" },
  service_area_activated: { label: "Service Area Activated", category: "status" },
  service_area_deactivated: { label: "Service Area Deactivated", category: "status" },
  service_area_archived: { label: "Service Area Archived", category: "status" },
  service_area_boundary_updated: { label: "Boundary Updated", category: "boundary" },
  service_area_team_assigned: { label: "Team Assignment Changed", category: "team" },
  service_area_team_unassigned: { label: "Team Assignment Changed", category: "team" },
  service_area_services_updated: { label: "Services Updated", category: "services" },
};

const FILTERS = [
  { value: "all", label: "All Changes" },
  { value: "status", label: "Status" },
  { value: "boundary", label: "Boundary" },
  { value: "team", label: "Team Assignments" },
  { value: "services", label: "Services" },
];

const describe = (e) => {
  const meta = EVENT_META[e.event_type] || { label: e.event_type, category: "status" };
  let detail = null;
  if (e.event_type === "service_area_team_assigned") detail = `Assigned to ${e.new_value}`;
  else if (e.event_type === "service_area_team_unassigned") detail = `Removed from ${e.old_value}`;
  else if (e.event_type === "service_area_services_updated") detail = `${(e.old_value || []).join(", ") || "—"} → ${(e.new_value || []).join(", ") || "—"}`;
  else if (e.event_type === "service_area_boundary_updated") detail = `${e.old_value || "—"} → ${e.new_value || "—"}`;
  else if (e.new_value && e.old_value) detail = `${e.old_value} → ${e.new_value}`;
  return { label: meta.label, category: meta.category, detail };
};

export const ServiceAreaHistoryPanel = ({ area, onClose }) => {
  const { API } = useAuth();
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/org/service-areas/${area.service_area_id}/audit`, { withCredentials: true });
        setEvents(data.events || []);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail));
      }
    })();
    return () => { document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area.service_area_id]);

  const filtered = (events || []).filter((e) => filter === "all" || (EVENT_META[e.event_type]?.category === filter));

  return (
    <div className="fixed inset-0 z-[1100] flex justify-end bg-slate-950/50" data-testid="sa-history-overlay">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-[#0B1220]" data-testid="sa-history-panel">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div>
            <p className="text-sm font-extrabold text-slate-950 dark:text-white">History</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{area.name}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="sa-history-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10"><X size={16} /></button>
        </div>

        <div className="border-b border-slate-200 px-5 py-3 dark:border-white/10">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sa-history-filter" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
            {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="sa-history-list">
          {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{error}</p>}
          {!events && !error && <p className="text-sm text-slate-400">Loading history…</p>}
          {events && filtered.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-slate-400" data-testid="sa-history-empty">
              <HistoryIcon size={22} className="text-slate-300 dark:text-slate-600" />
              No changes recorded {filter !== "all" ? "for this filter" : "yet"}.
            </div>
          )}
          {filtered.length > 0 && (
            <ol className="space-y-4 border-l border-slate-200 pl-4 dark:border-white/10">
              {filtered.map((e) => {
                const { label, detail } = describe(e);
                return (
                  <li key={e.event_id} className="relative" data-testid={`sa-history-event-${e.event_id}`}>
                    <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-[#0052FF]" />
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
                    {detail && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{detail}</p>}
                    <p className="mt-1 text-[11px] font-semibold text-slate-400 dark:text-slate-500">{e.actor_name} · {formatDateTime(e.timestamp)}</p>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};
